import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildFuelCostsPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import {
  InventoryEvInsightsEntities,
  InventoryEvInsightsModel,
  InventoryEvInsightsPaths,
} from 'client/data/models/inventory-ev-insights';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
// Utils
import {
  isBatteryRangeModuleEnabled,
  isRecurrentAvailable,
} from 'site-modules/shared/utils/inventory/is-recurrent-available';
import { getBatteryWarrantyData } from 'site-modules/shared/components/inventory/ev-insights/utils/ev-insights';
import { EV_CREATIVE_IDS } from 'site-modules/shared/components/inventory/ev-insights/ev-insights-constants';
import { checkPersistedWithStateCodeMatchWizardContext } from 'site-modules/shared/components/incentives/incentives-wizard/utils/is-persisted-wizard-state-applicable';
// Components
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { GradientIcon } from 'site-modules/shared/components/inventory/ev-insights/components/gradient-icon/gradient-icon';
import { BatteryRangeAndCharging } from 'site-modules/shared/components/inventory/ev-insights/components/battery-range-and-charging/battery-range-and-charging';
import { ChargingInsight } from 'site-modules/shared/components/charging-insight/charging-insight';
import { IncentivesEvInsightWizard } from 'site-modules/shared/components/inventory/ev-insights/components/incentives-insight/incentives-ev-insight-wizard';
import { CostToDriveInsight } from 'site-modules/shared/components/inventory/ev-insights/components/cost-to-drive-insight/cost-to-drive-insight';
import { ReadyForEv, ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { IncentivesWizardWidget } from 'site-modules/shared/components/incentives/incentives-wizard/incentives-wizard-widget';
import { LOAN_PAYMENT, LEASE_PAYMENT } from 'site-modules/shared/constants/allowed-inventory-request-params';

import './ev-insights.scss';

const EvInsightsUI = memo(
  ({
    vinData,
    isMobile = false,
    evInsightsData = null,
    fuelCostsData = null,
    visitorLocation = null,
    isPurchase = false,
    withCollapse = false,
  }) => {
    const costPerMonth = get(fuelCostsData, 'fuelCosts.costPerMonth', 0);
    const { make, model, year } = get(vinData, 'vehicleInfo.styleInfo', {});
    const isRecurrentDataAvailable = isRecurrentAvailable(vinData);

    const { showBatteryWarrantyModule, warrantyDisclaimer, warrantyHeader, warrantyText } = getBatteryWarrantyData(
      evInsightsData,
      vinData
    );

    const vdpModuleCommonProps = {
      className: 'bg-white mb-1',
      headerTag: 'h3',
      titleClassName: 'heading-4',
      indentationSize: 1.5,
    };

    const collapseConfig = withCollapse
      ? { minHeight: isMobile ? '600px' : '500px', scrollOffset: -90, btnTrackingId: 'view_more_info' }
      : null;
    const incentives = get(evInsightsData, 'incentives', []);

    const locationZip = get(visitorLocation, 'zipCode');
    const locationStateCode = get(visitorLocation, 'stateCode');
    const dealerZip = get(vinData, 'dealerInfo.address.zip', locationZip);
    const dealerStateCode = get(vinData, 'dealerInfo.address.stateCode', locationStateCode);
    const engineType = get(vinData, 'vehicleInfo.partsInfo.engineType', '');

    const readyForEv = (
      <VdpModule {...vdpModuleCommonProps} sectionTitle="Am I Ready for an EV?">
        <ReadyForEv hideCta isMobileLayout />
      </VdpModule>
    );

    return (
      <VdpModule
        className="ev-insights bg-cool-gray-90 mb-1_5"
        sectionTitle={
          <>
            <GradientIcon icon="icon-power2" iconClassName="size-20 me-0_5" />
            EV Insights
          </>
        }
        titleClassName="heading-3 mt-0_5"
        subCopy={`${year} ${make} ${model}`}
        subCopyClassName="text-gray-darkest fw-normal mb-1_5"
        data-tracking-parent={EV_CREATIVE_IDS.EV_MODULE}
        collapseConfig={collapseConfig}
      >
        {isBatteryRangeModuleEnabled(vinData) && (
          <VdpModule {...vdpModuleCommonProps} sectionTitle="Range & Battery">
            <BatteryRangeAndCharging vinData={vinData} isMobile={isMobile} isSimplified={!isRecurrentDataAvailable} />
          </VdpModule>
        )}

        <div className="insights-cards">
          {showBatteryWarrantyModule && isMobile && (
            <VdpModule
              {...vdpModuleCommonProps}
              sectionTitle={warrantyHeader}
              data-tracking-parent={EV_CREATIVE_IDS.WARRANTY}
            >
              <div className="size-24 fw-bold text-cool-gray-30 text-center mb-0_25">{warrantyText}</div>
            </VdpModule>
          )}
          <VdpModule
            {...vdpModuleCommonProps}
            sectionTitle="Charging at Home"
            data-tracking-parent={EV_CREATIVE_IDS.CHARGING_AT_HOME}
          >
            <ChargingInsight
              chargeTime={get(evInsightsData, 'style.epaSpecifications.batteryChargeTime')}
              portType={get(evInsightsData, 'style.engine.electricMotor.fastChargePortType')}
              creativeId={EV_CREATIVE_IDS.CHARGING_AT_HOME}
              noAdditionalCopy
            />
          </VdpModule>

          <VdpModule
            {...vdpModuleCommonProps}
            sectionTitle="Tax Credits & Rebates"
            data-tracking-parent={EV_CREATIVE_IDS.TAX_CREDITS_AND_REBATES}
          >
            <IncentivesWizardWidget
              availableIncentives={incentives}
              zipCode={dealerZip}
              stateCode={dealerStateCode}
              inventoryType={vinData.type}
              paymentType={isPurchase ? LOAN_PAYMENT : LEASE_PAYMENT}
              engineTypes={[engineType.toLowerCase()]}
              evaluatePersistedStateMatchWizardContext={checkPersistedWithStateCodeMatchWizardContext}
            >
              {({ wizardState, incentives: wizardIncentives, onFinish, onStartOver }) => (
                <IncentivesEvInsightWizard
                  incentives={wizardIncentives}
                  vinData={vinData}
                  isMobile={isMobile}
                  wizardState={wizardState}
                  onFinish={onFinish}
                  onStartOver={onStartOver}
                />
              )}
            </IncentivesWizardWidget>
          </VdpModule>

          {showBatteryWarrantyModule && !isMobile && (
            <VdpModule
              {...vdpModuleCommonProps}
              sectionTitle={warrantyHeader}
              data-tracking-parent={EV_CREATIVE_IDS.WARRANTY}
            >
              <div className="size-24 fw-bold text-cool-gray-30 text-center mb-0_25">{warrantyText}</div>
            </VdpModule>
          )}
          {!!costPerMonth && (
            <VdpModule
              {...vdpModuleCommonProps}
              sectionTitle="Cost to Drive"
              data-tracking-parent={EV_CREATIVE_IDS.COST_TO_DRIVE}
            >
              <CostToDriveInsight
                fuelCostsData={fuelCostsData}
                stateName={get(visitorLocation, 'state')}
                model={model}
              />
            </VdpModule>
          )}
          <TreehousePromotionCheck fallback={readyForEv}>
            <VdpModule
              {...vdpModuleCommonProps}
              sectionTitle="Am I Ready for an EV?"
              data-tracking-parent={EV_CREATIVE_IDS.TREEHOUSE_PROMOTION}
            >
              <ReadyForEvTreehouse isMobileLayout creativeId={EV_CREATIVE_IDS.TREEHOUSE_PROMOTION} />
            </VdpModule>
          </TreehousePromotionCheck>
        </div>

        {warrantyDisclaimer && <div className="size-12 text-cool-gray-40 mt-1">{warrantyDisclaimer}</div>}
      </VdpModule>
    );
  }
);

EvInsightsUI.displayName = 'EvInsightsUI';

EvInsightsUI.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  evInsightsData: InventoryEvInsightsEntities.InventoryEvInsightsData,
  isMobile: PropTypes.bool,
  fuelCostsData: VehicleEntities.StyleFuelCostData,
  visitorLocation: VisitorEntities.Location,
  withCollapse: PropTypes.bool,
  isPurchase: PropTypes.bool,
};

const stateToPropsConfig = {
  evInsightsData: bindToPath(
    ({ vinData: { vin }, isPurchase }) =>
      InventoryEvInsightsPaths.getEvInsightsAllProgramIncentivesDataPath(
        vin,
        isPurchase ? LOAN_PAYMENT : LEASE_PAYMENT
      ),
    InventoryEvInsightsModel
  ),
  fuelCostsData: bindToPath(({ vinData }) => {
    const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId');
    return buildFuelCostsPath(styleId);
  }, VehicleModel),
  visitorLocation: bindToPath('location', VisitorModel),
};

export const EvInsights = connectToModel(EvInsightsUI, stateToPropsConfig);
